import { useEffect } from 'react'
import { Link, useNavigate, useParams, useSearchParams, useOutletContext } from 'react-router-dom'
import { useComputed } from '@preact/signals-react'

import Breadcrumb from 'react-bootstrap/Breadcrumb'
import Col from 'react-bootstrap/Col'
import Form from 'react-bootstrap/Form'
import Row from 'react-bootstrap/Row'
import Stack from 'react-bootstrap/esm/Stack.js'

import DebugAccordion from '../../components/DebugAccordion'
import Loadable from '../../components/Loadable'
import PageTitle from '../../components/PageTitle.jsx'
import ProfileNav from './components/ProfileNav.jsx'
import ProfileTools from './components/ProfileTools.jsx'
import TimestampWithUTCTooltip from '../../components/TimestampWithUTCTooltip'

import { addNotification, NotificationTypes } from '../../state/actions/notificationActions.js'
import { fetchProfile } from '../../state/actions/profileActions'
import { fetchSchemaManifest } from '../../state/actions/schemaManifestActions'

import { profileSignal, profileLoadedSignal, profileErrorSignal } from '../../state/signals/profileSignals'
import { schemaManifestSignal } from '../../state/signals/schemaManifestSignals'

import useRedirectOnBeIdChange from '../../hooks/useRedirectOnBeIdChange.js'

export default function ProfileDetail() {

    const navigate = useNavigate()
    const [searchParams] = useSearchParams()
    const debug = !!searchParams.get('debug')
    const { beId, profileId } = useParams()
    const beIdChanged = useRedirectOnBeIdChange(beId, `/be/${beId}/profiles`)
    const { user, config, accessToken, userPermissions, userPermissionsSummary, accordionData: sidebarAccordionData } = useOutletContext()

    useEffect(() => {
        if (userPermissionsSummary.value && !userPermissionsSummary.value.canViewProfiles) {
            addNotification('You are not authorized to view UTG profiles for this account.', NotificationTypes.ERROR, true)
            navigate(`/`)
        }
    }, [userPermissionsSummary.value, navigate])

    const canEditComputed = useComputed(() => {
        return userPermissionsSummary?.value?.canEditProfiles
    })

    useEffect(() => {
        if (beIdChanged) return
        fetchProfile(beId, profileId, config, accessToken)
        fetchSchemaManifest()
    }, [beId, beIdChanged, profileId, config, accessToken])

    const accordionData = [
        { name: 'Profile Data', data: profileSignal.value },
        { name: 'Access Token', data: accessToken },
        { name: 'App Config', data: config },
        { name: 'Schema Manifest', data: schemaManifestSignal.value }
    ]

    return (
        <>
            <Breadcrumb className="px-3 pt-1 bg-light">
                <Breadcrumb.Item linkAs={Link} linkProps={{ to: `/be/${beId}/profiles` }}>
                    Profile List
                </Breadcrumb.Item>
                <Breadcrumb.Item active>{profileLoadedSignal.value ? <>Manage {profileSignal.value.name}</> : <>Loading...</>
                }</Breadcrumb.Item>
            </Breadcrumb>
            <Stack direction="horizontal" gap={3}>
                <PageTitle
                    title={
                        profileLoadedSignal.value ? `Manage Profile ${profileSignal.value.name}` : "Loading..."
                    }
                    canEditSignal={canEditComputed}
                />
                <Loadable loadedSignals={[profileLoadedSignal]} errorSignals={[profileErrorSignal]}>
                    <ProfileTools profile={profileSignal.value}></ProfileTools>
                </Loadable>
            </Stack>
            <ProfileNav activeKey="1"></ProfileNav>
            <Form>
                <Form.Group as={Row} className="mb-3">
                    <Form.Label column sm="2" className="text-end">Profile Name</Form.Label>
                    <Col sm="10"><Form.Control disabled readOnly value={profileLoadedSignal.value ? profileSignal.value?.name : "Loading..."} /></Col>
                </Form.Group>
                <Form.Group as={Row} className="mb-3">
                    <Form.Label column sm="2" className="text-end">Profile Version</Form.Label>
                    <Col sm="10"><Form.Control disabled readOnly value={profileLoadedSignal.value ? profileSignal.value?.version : "Loading..."} /></Col>
                </Form.Group>
                <Form.Group as={Row} className="mb-3">
                    <Form.Label column sm="2" className="text-end">Parent Account</Form.Label>
                    <Col sm="10"><Form.Control disabled readOnly value={profileLoadedSignal.value ? profileSignal.value?.businessEntitySummary?.name : "Loading..."} /></Col>
                </Form.Group>
                <Form.Group as={Row} className="mb-3">
                    <Form.Label column sm="2" className="text-end">Created</Form.Label>
                    <Col sm="10"><span><TimestampWithUTCTooltip>{profileSignal.value?.createdAt}</TimestampWithUTCTooltip></span></Col>
                </Form.Group>
                <Form.Group as={Row} className="mb-3">
                    <Form.Label column sm="2" className="text-end">Last Updated</Form.Label>
                    <Col sm="10"><span><TimestampWithUTCTooltip>{profileSignal.value?.lastUpdatedAt}</TimestampWithUTCTooltip></span></Col>
                </Form.Group>
                <Form.Group as={Row} className="mb-3">
                    <Form.Label column sm="2" className="text-end">Last Applied</Form.Label>
                    <Col sm="10"><span><TimestampWithUTCTooltip>{profileSignal.value?.lastAppliedAt}</TimestampWithUTCTooltip></span></Col>
                </Form.Group>
                <Form.Group as={Row} className="mb-3">
                    <Form.Label column sm="2" className="text-end">Id</Form.Label>
                    <Col sm="10"><Form.Control disabled readOnly value={profileLoadedSignal.value ? profileSignal.value?.id : "Loading..."} /></Col>
                </Form.Group>
            </Form>
            <DebugAccordion data={accordionData} debug={debug} />
        </>
    )
}